import * as React from "react";
import {useEffect} from "react";
import {translate} from "../../translate/translate";
import {
  Box,
  Button,
  Fab,
  FormControl,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import TablePaginationComponent from "../../components/table/table-pagination.component";
import {Add, Search} from "@mui/icons-material";
import {PaginatedResult} from "../../models/default/paginated-result";
import TableActionComponent from "../../components/table/table-action.component";
import {Field, Form, Formik, FormikHelpers} from "formik";
import {ErrorComponent} from "../../components/error/error.component";
import GenericDialog from "../../components/dialog/generic-dialog.component";
import * as Yup from "yup";
import {Client} from "../../models/basic/client";
import {useFilters} from "../../filters/base-filter";
import {usePagination} from "../../utils/pagination";
import {ClientFilter, ClientService} from "../../services/client-service";
import { toast } from "react-toastify";


const ClientComponent = () => {
    const service = new ClientService();
    const {filters, updateFilter, updateFilterKeepingParam, resetFilters } = useFilters<ClientFilter>(
        { search: "", active: ""}, ["search", "active"]
    );

    const [clientList, setClientList] = React.useState<PaginatedResult<Client>>();

    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<Client>(clientList);

    const url = "/registration/client/";

    const [object, setObject] = React.useState(new Client());
    const [openDialog, setOpenDialog] = React.useState(false);
    const [invalidForm, setInvalidForm] = React.useState(true);
    const [isEdit, setIsEdit] = React.useState(false);

    const schema = Yup.object().shape({
        name: Yup.string()
            .required(),
        sap_code: Yup.string().required(),
    });

    const handleOpenEdit = (row: Client) => {
        setIsEdit(true);
        setObject(row);
        setInvalidForm(!row.name.trim() || !row.sap_code?.trim());
        setOpenDialog(true);
    }

    const handleOpenNew = () => {
        setIsEdit(false);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setInvalidForm(true);
        setOpenDialog(false);
        setObject(new Client());
    };

    const search = (restartIndex = false) => {
        service.readPaginated(filters, [], pagination).then(response => {
            setClientList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });

        restartIndex && resetPage();
    };

    useEffect(() => {
        search();
    }, [pagination]);

    const handleDelete = (row: Client) => {
        service.delete(Number(row.id)).then(() => {
            toast.success(translate.t("successfully_deleted"));
            resetFilters();
            resetPage();
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    const handleSwitch = (row: Client, column: keyof Client) => {
        (row[column] as boolean) = !row[column];
        const payload = {"active": row.active};
        service.patch(Number(row.id), payload).then(() => {
            toast.success(translate.t("successfully_updated"));
            search();
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    };

    const handleSubmit = (values: Client) => {
        setTimeout(() => {
            const client = new Client();
            const formattedValues = {
                ...values,
                name: values.name.trim(),
                sap_code: values.sap_code?.trim(),
                plant: values.plant?.trim(),
            };
            Object.assign(client, formattedValues);
            if (client.id !== undefined && client.id !== 0) {
                service.patch(client.id, client).then(() => {
                    toast.success(translate.t("successfully_updated"));
                    handleCloseDialog();
                    search(true);
                }).catch((error: any) => {
                    ErrorComponent(error);
                });
            } else {
                const { id: _, ...newObj } = client;
                service.create(newObj as any)
                    .then(() => {
                        toast.success(translate.t("successfully_registered"));
                        handleCloseDialog();
                        search(true);
                    })
                    .catch((error: any) => {
                        ErrorComponent(error);
                    });
            }
        }, 500);
    }

    const handleObjChange = (values: Client) => {
        setObject(values);
        setInvalidForm(!values.name.trim() || !values.sap_code?.trim() || !values.plant?.trim());
    }

    const customCellRender = (column: keyof Client, index: number, row: Client) => {
        if (column === "sap_code") {
            return row["sap_code"] ? row["sap_code"] : "-";
        }
        if (column === "plant") {
            return row["plant"] ? row["plant"] : "-";
        }
        return row[column];
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("clients")}
                </Typography>
            </div>
            <div className="floating-button">
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Add/>}
                    onClick={() => {
                        handleOpenNew();
                    }}>{translate.t("new_client").toUpperCase()}</Button>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Grid alignItems="center" container spacing={2} direction={"row"} width="50%">
                                            <Grid size={5}>
                                                <Field
                                                    as={TextField}
                                                    name="search"
                                                    label={""}
                                                    fullWidth
                                                    variant={"outlined"}
                                                    autoFocus={true}
                                                    placeholder={translate.t("search_client")}
                                                    value={filters.search}
                                                    onChange={(event: React.ChangeEvent<{
                                                        value: unknown
                                                    }>) => updateFilterKeepingParam("search", event.target.value)}
                                                />
                                            </Grid>
                                            <Grid >
                                                <Fab
                                                    size="small"
                                                    type="submit"
                                                    color="secondary">
                                                    <Search/>
                                                </Fab>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["client", "sapCode", "Plant", "Status", "action"]}
                    displayedColumns={["name", "sap_code", "plant", "active", "action"]}
                    handleDelete={handleDelete}
                    handleSwitch={handleSwitch}
                    editWithDialog={handleOpenEdit}
                    url={url}
                    handleDoubleClick={handleOpenEdit}
                    count={clientList?.count}
                    next={clientList?.next}
                    previous={clientList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    actionsComponent={TableActionComponent}
                    columnStyles={{1:"35%", 2:"35%", 3:"15%", 4:"15%"}}
                    customCellRender={customCellRender}
                    customColumns={["sap_code", "plant"]}
                    showActions={["edit", "delete"]}
                />
            </Paper>
            <GenericDialog
                open={openDialog}
                onClose={handleCloseDialog}
                title={isEdit ? translate.t("edit_client") : translate.t("new_client")}
                colorButton={"primary"}
                labelButton={"save"}
                disableConfirm={invalidForm}
                handleConfirm={() => handleSubmit(object)}>
                <Box>
                    <FormControl fullWidth>
                        <Formik
                            id="dialog-form"
                            enableReinitialize
                            initialValues={object}
                            validationSchema={schema}
                            onSubmit={(values: Client, {resetForm}: FormikHelpers<Client>) => {
                                handleSubmit(values);
                                resetForm();
                                handleCloseDialog();
                            }}
                            validateOnChange={true}>
                            {({isValid, values, isValidating, errors, touched}) => (
                                <Form autoComplete="off">
                                    <Grid container spacing={2} direction={"column"}>
                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="name"
                                                label={translate.t("name")}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={true}
                                                required={true}
                                                values={values}
                                                error={touched.name && Boolean(errors.name) && invalidForm}
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    if (value.length > 256) return;
                                                    handleObjChange({
                                                        ...obj,
                                                        name: value as string,
                                                    });
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.name && Boolean(errors.name) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>
                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="sap_code"
                                                label={translate.t("sapCode")}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={false}
                                                required={true}
                                                values={values}
                                                error={touched.sap_code && Boolean(errors.sap_code) && invalidForm}
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    if (value.length > 256) return;
                                                    handleObjChange({
                                                        ...obj,
                                                        sap_code: value as string,
                                                    })
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.sap_code && Boolean(errors.sap_code) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>

                                        <Grid >
                                            <Field
                                                as={TextField}
                                                name="plant"
                                                label={"Plant"}
                                                variant={"outlined"}
                                                fullWidth
                                                autoFocus={false}
                                                required={true}
                                                values={values}
                                                error={touched.plant && Boolean(errors.plant) && invalidForm}
                                                onChange={(event: React.ChangeEvent<{
                                                    value: unknown
                                                }>) => {
                                                    const obj = object;
                                                    const value = event.target.value as string;
                                                    if (value.length > 256) return;
                                                    handleObjChange({
                                                        ...obj,
                                                        plant: value as string,
                                                    })
                                                }}
                                                InputProps={{
                                                    maxLength: 54,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {touched.plant && Boolean(errors.plant) && invalidForm && (
                                                                <div className={"required-field"}>
                                                                    {translate.t("required_field")}
                                                                </div>
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}/>
                                        </Grid>
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    </FormControl>
                </Box>
            </GenericDialog>
        </section>
    );
};

export default ClientComponent;
